<script setup lang="ts">
import { getTechnologyCategoryLabelForTechnologyType, getTechnologyTypeLabel, Product, ProductTechnology, Technology, TechnologyCategory, TechnologyType, technologyTypesByCategory } from "@/model";
import { CheckCircleIcon } from '@heroicons/vue/20/solid';

import Dialog from '@/components/Dialog.vue'
import { pb } from "@/pocketbase";
import { computed, reactive, ref } from "vue";
import Input from "@/components/Input.vue";
import Button from "@/components/Button.vue";
import Success from "@/components/Success.vue";
import Heading from "@/components/Heading.vue";

const props = defineProps<{
  product: Product,
}>()

const searchTerm = ref('');

const technologies: Technology[] = await pb.collection('technologies').getFullList<Technology>(null, {
  sort: 'type,name',
});

const groupedTechnologies = computed(
    () => {
      const groups: Record<string, {
        category: TechnologyCategory,
        types: Record<string, {
          type: TechnologyType,
          technologies: Technology[],
        }>,
      }> = {};

      for (const { category, types } of technologyTypesByCategory) {
        for (const type of types) {
          for (const technology of technologies) {
            if (technology.name.toLowerCase().indexOf(searchTerm.value.toLowerCase()) === -1) {
              continue;
            }

            const technologyTypeLabel = getTechnologyTypeLabel(technology.type);
            if (technologyTypeLabel !== type) {
              continue;
            }

            const technologyCategoryLabel = getTechnologyCategoryLabelForTechnologyType(technology.type);
            if (technologyCategoryLabel !== category) {
              continue;
            }

            if (!groups[technologyCategoryLabel]) {
              groups[technologyCategoryLabel] = {
                category: technologyCategoryLabel,
                types: [],
              }
            }

            if (!groups[technologyCategoryLabel].types[technologyTypeLabel]) {
              groups[technologyCategoryLabel].types[technologyTypeLabel] = {
                type: technologyTypeLabel,
                technologies: [],
              }
            }

            groups[technologyCategoryLabel].types[technologyTypeLabel].technologies.push(technology);
          }
        }
      }

      return groups;
    },
);

const initialProductTechnologies = await pb.collection('product_technologies').getFullList<ProductTechnology>(null, {
  expand: 'technology',
  sort: 'technology.name',
  filter: `product.id = '${props.product.id}'`,
});

const state: {
  saved: boolean,
  saving: boolean,
  savedProductTechnologies: ProductTechnology[],
  unsavedProductTechnologies: ProductTechnology[],
} = reactive({
  saved: false,
  saving: false,
  savedProductTechnologies: initialProductTechnologies,
  unsavedProductTechnologies: initialProductTechnologies,
});

function toggleTechnology(technology: Technology) {
  const productTechnology = state.unsavedProductTechnologies.find(pt => pt.technology === technology.id);

  if (productTechnology) {
    state.unsavedProductTechnologies = state.unsavedProductTechnologies.filter(pt => pt.technology !== technology.id);
  } else {
    const previouslySaved = state.savedProductTechnologies.find(pt => pt.technology === technology.id);

    state.unsavedProductTechnologies.push({
      id: previouslySaved?.id || '',
      product: props.product.id,
      technology: technology.id,
      version: previouslySaved?.version || '',
      comment: previouslySaved?.comment || '',
    });
  }
}

async function save() {
  state.saved = false;
  state.saving = true;

  const savedTechnologyIds = state.savedProductTechnologies.map(pt => pt.technology);
  const unsavedTechnologyIds = state.unsavedProductTechnologies.map(pt => pt.technology);
  const deselectedTechnologyIds = savedTechnologyIds.filter(id => !unsavedTechnologyIds.includes(id));

  for (const technologyId of deselectedTechnologyIds) {
    await pb.collection('product_technologies').delete(
        state.savedProductTechnologies.find(pt => pt.technology == technologyId)?.id
    );
  }

  const savedProductTechnologies = [];
  for (const productTechnology of state.unsavedProductTechnologies) {
    if (productTechnology.id === '') {
      savedProductTechnologies.push(await pb.collection('product_technologies').create<ProductTechnology>({
        product: productTechnology.product,
        technology: productTechnology.technology,
        version: productTechnology.version,
        comment: productTechnology.comment,
      }));
    } else {
      await pb.collection('product_technologies').update(productTechnology.id, {
        version: productTechnology.version,
        comment: productTechnology.comment,
      });
      savedProductTechnologies.push(productTechnology);
    }
  }

  state.saved = true;
  state.saving = false;
  state.savedProductTechnologies = savedProductTechnologies;
  state.unsavedProductTechnologies = savedProductTechnologies;
}
</script>

<template>
  <Dialog @close="$emit('close')" :title="`Edit ${product.name}`" description="Change the technologies of the product">
    <div class="relative pb-12">
      <div class="sticky top-32 z-20">
        <Button label="Save changes" outline fill class="mb-4" :loading="state.saving" :disabled="state.saving" @click.prevent="save"/>
        <Success v-if="state.saved" class="mb-4">Saved!</Success>
        <Input v-model="searchTerm" label="Search term" placeholder="Search technology ..." marginless/>
      </div>

      <template v-for="group in groupedTechnologies">
        <Heading>{{ group.category }}</Heading>
        <table>
          <template v-for="subgroup in Object.values(group.types)">
            <thead>
            <tr>
              <th class="w-8"></th>
              <th>
                <div class="-ml-8">{{ subgroup.type }}</div>
              </th>
              <th class="w-24">Version</th>
              <th>Comment</th>
            </tr>
            </thead>
            <tbody v-for="technology in subgroup.technologies">
            <tr class="h-12" v-for="productTechnology in [state.unsavedProductTechnologies.find(pt => pt.technology === technology.id)]">
              <td>
                <CheckCircleIcon @click="toggleTechnology(technology)" :class="['icon', productTechnology ? '' : 'opacity-20']"/>
              </td>
              <td :class="['cursor-pointer', productTechnology ? '' : 'opacity-50']" @click="toggleTechnology(technology)">
                {{ technology.name }}
              </td>
              <template v-if="productTechnology">
                <td><Input label="Version" v-model="productTechnology.version" class="text-xs" marginless/></td>
                <td><Input label="Comment" v-model="productTechnology.comment" class="text-xs" marginless/></td>
              </template>
              <template v-else>
                <td colspan="2"/>
              </template>
            </tr>
            </tbody>
          </template>
        </table>
      </template>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
table {
  @apply table-fixed w-full mb-6;
}

th, td {
  @apply border-y border-gray-700 p-1 text-left;
}

th {
  @apply pt-6;
}

.icon {
  @apply w-5 h-5 cursor-pointer;
}
</style>
